import { Card } from "@/components/ui/card";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { TrendingUp, TrendingDown, DollarSign, Percent, BarChart3 } from "lucide-react";
import { cn } from "@/lib/utils";

export default function FinancialTrendAnalysis({ candidate, activities }) {
  // Prepare price trend data
  const priceData = activities
    .filter(a => a.price_discussed)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(a => ({
      date: new Date(a.date).toLocaleDateString("no-NO", { month: "short", day: "numeric" }),
      pris: a.price_discussed / 1000000, // Convert to millions
      fullDate: a.date,
    }));

  // Calculate metrics
  const avgPrice = priceData.length > 0
    ? priceData.reduce((sum, d) => sum + d.pris, 0) / priceData.length
    : 0;

  const priceChange = priceData.length >= 2
    ? ((priceData[priceData.length - 1].pris - priceData[0].pris) / priceData[0].pris) * 100
    : 0;

  // EBITDA margin calculation
  const ebitdaMargin = candidate.ebitda && candidate.revenue
    ? (candidate.ebitda / candidate.revenue) * 100
    : null;

  // Valuation metrics
  const metrics = [
    {
      label: "Gjennomsnittlig Pris",
      value: `${avgPrice.toFixed(1)} MNOK`,
      change: priceChange,
      icon: DollarSign,
    },
    {
      label: "Siste Pris",
      value: priceData.length > 0 ? `${priceData[priceData.length - 1].pris.toFixed(1)} MNOK` : "N/A",
      icon: DollarSign,
    },
    {
      label: "EBITDA Margin",
      value: ebitdaMargin ? `${ebitdaMargin.toFixed(1)}%` : "N/A",
      icon: Percent,
    },
    {
      label: "EV/EBITDA Multipel",
      value: candidate.multiple ? `${candidate.multiple}x` : "N/A",
      icon: TrendingUp,
    },
  ];

  // Comparison data for multiples
  const industryAverage = 8.5; // Example industry average
  const multiplesData = candidate.multiple ? [
    { name: "Kandidat", multipel: candidate.multiple },
    { name: "Bransje snitt", multipel: industryAverage },
  ] : [];

  // Check if we have minimum data
  const hasMinimalData = candidate.revenue || candidate.ebitda || priceData.length > 0;

  if (!hasMinimalData) {
    return (
      <Card className="p-8 bg-white border-0 shadow-sm text-center">
        <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Ingen trenddata tilgjengelig</h3>
        <p className="text-slate-500">
          Legg til omsetning, EBITDA eller prisdiskusjoner i aktiviteter for å se trendanalyse.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric, idx) => (
          <Card key={idx} className="p-4 bg-white border-0 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-lime-100 flex items-center justify-center">
                <metric.icon className="w-4 h-4 text-zinc-900" />
              </div>
              <p className="text-xs text-slate-500">{metric.label}</p>
            </div>
            <p className="text-xl font-bold text-slate-900">{metric.value}</p>
            {metric.change !== undefined && (
              <div className="flex items-center gap-1 mt-1">
                {metric.change >= 0 ? (
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-rose-600" />
                )}
                <span className={cn("text-sm font-medium", metric.change >= 0 ? "text-emerald-600" : "text-rose-600")}>
                  {metric.change >= 0 ? "+" : ""}{metric.change.toFixed(1)}%
                </span>
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Price Trend Chart */}
      {priceData.length > 1 && (
        <Card className="p-6 bg-white border-0 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-4">Prisutviklig over tid</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={priceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#64748b" style={{ fontSize: "12px" }} />
              <YAxis stroke="#64748b" style={{ fontSize: "12px" }} label={{ value: "MNOK", angle: -90, position: "insideLeft" }} />
              <Tooltip
                contentStyle={{ backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px" }}
                formatter={(value) => [`${value.toFixed(2)} MNOK`, "Pris"]}
              />
              <Line type="monotone" dataKey="pris" stroke="#c9e25a" strokeWidth={2} dot={{ fill: "#c9e25a", r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Multiple Comparison */}
      {multiplesData.length > 0 && (
        <Card className="p-6 bg-white border-0 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-4">Multipel-sammenligning</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={multiplesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" style={{ fontSize: "12px" }} />
              <YAxis stroke="#64748b" style={{ fontSize: "12px" }} />
              <Tooltip
                contentStyle={{ backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px" }}
                formatter={(value) => [`${value}x`, "Multipel"]}
              />
              <Bar dataKey="multipel" fill="#c9e25a" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          {candidate.multiple && (
            <p className="text-sm text-slate-600 mt-4">
              {candidate.multiple < industryAverage ? (
                <span className="text-emerald-600 font-medium">
                  Multipel er {((industryAverage - candidate.multiple) / industryAverage * 100).toFixed(0)}% lavere enn bransjesnittet
                </span>
              ) : (
                <span className="text-amber-600 font-medium">
                  Multipel er {((candidate.multiple - industryAverage) / industryAverage * 100).toFixed(0)}% høyere enn bransjesnittet
                </span>
              )}
            </p>
          )}
        </Card>
      )}

      {/* Financial Ratios */}
      {(candidate.revenue || candidate.ebitda) && (
        <Card className="p-6 bg-white border-0 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-4">Finansielle Nøkkeltall</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {candidate.revenue && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Omsetning</p>
                <p className="text-2xl font-bold text-slate-900">
                  {(candidate.revenue / 1000000).toFixed(1)} MNOK
                </p>
              </div>
            )}
            {candidate.ebitda && (
              <div>
                <p className="text-sm text-slate-500 mb-1">EBITDA</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {(candidate.ebitda / 1000000).toFixed(1)} MNOK
                </p>
              </div>
            )}
            {ebitdaMargin && (
              <div>
                <p className="text-sm text-slate-500 mb-1">EBITDA Margin</p>
                <p className="text-2xl font-bold text-zinc-900">
                  {ebitdaMargin.toFixed(1)}%
                </p>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}